import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  UserCheck, Plus, Edit2, Trash2, Search, Phone, Mail,
  Bike, CheckCircle, XCircle, Clock, Loader2, AlertCircle, X, Save, RefreshCw, ChevronDown, ChevronUp,
  UserPlus, MessageSquare, KeyRound, Copy,
} from 'lucide-react';
import deliveryAgentService from '../../services/deliveryAgentService';
import { fuzzyMatch } from '../../lib/fuzzySearch';

const VEHICLE_LABELS = { bike: 'Bike', bicycle: 'Bicycle', scooter: 'Scooter', van: 'Van', other: 'Other' };
const VEHICLE_OPTIONS = Object.entries(VEHICLE_LABELS);
const STATUS_OPTIONS = [
  { value: 'active',   label: 'Active',   color: 'bg-green-100 text-green-700' },
  { value: 'inactive', label: 'Inactive', color: 'bg-gray-100 text-gray-600'   },
  { value: 'on_leave', label: 'On Leave', color: 'bg-amber-100 text-amber-700' },
];
const AVAIL_STYLE = {
  available:   'bg-green-100 text-green-700',
  on_delivery: 'bg-blue-100 text-blue-700',
};
const AVAIL_LABEL = { available: 'At Warehouse', on_delivery: 'On Delivery' };

const statusStyle  = (s) => STATUS_OPTIONS.find(o => o.value === s)?.color || 'bg-gray-100 text-gray-600';
const statusLabel  = (s) => STATUS_OPTIONS.find(o => o.value === s)?.label || s;

const emptyForm = { name: '', phone: '', email: '', vehicleType: 'bike', vehicleNo: '', status: 'active', notes: '' };

// ── Agent Modal ─────────────────────────────────────────────────────────────
const AgentModal = ({ agent, onClose, onSave }) => {
  const [form, setForm]         = useState(agent ? { ...emptyForm, ...agent, email: agent.email || '', vehicleNo: agent.vehicleNo || '', notes: agent.notes || '' } : emptyForm);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name.trim())  return setError('Name is required');
    if (!form.phone.trim()) return setError('Phone is required');
    if (!/^\d{10}$/.test(form.phone.trim())) return setError('Phone must be 10 digits');
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return setError('Invalid email address');

    setSaving(true); setError('');
    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim() || undefined,
      vehicleType: form.vehicleType,
      vehicleNo: form.vehicleNo.trim() || undefined,
      status: form.status,
      notes: form.notes.trim() || undefined,
    };
    const result = agent
      ? await deliveryAgentService.update(agent.id, payload)
      : await deliveryAgentService.create(payload);

    if (result.success) { onSave(); onClose(); }
    else { setError(result.error || 'Failed to save'); }
    setSaving(false);
  };

  const inputCls = 'w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-800">{agent ? 'Edit Agent' : 'Add New Agent'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Full Name *</label>
              <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Ramesh Kumar" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Phone *</label>
              <input className={inputCls} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="10-digit number" maxLength={10} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Email <span className="text-gray-400">(optional)</span></label>
              <input className={inputCls} value={form.email} onChange={e => set('email', e.target.value)} placeholder="agent@example.com" type="email" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Vehicle Type</label>
              <select className={inputCls} value={form.vehicleType} onChange={e => set('vehicleType', e.target.value)}>
                {VEHICLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Vehicle Number <span className="text-gray-400">(optional)</span></label>
              <input className={inputCls} value={form.vehicleNo} onChange={e => set('vehicleNo', e.target.value)} placeholder="e.g. RJ14 AB 1234" />
            </div>
            {agent && (
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select className={inputCls} value={form.status} onChange={e => set('status', e.target.value)}>
                  {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            )}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Notes <span className="text-gray-400">(optional)</span></label>
              <textarea className={inputCls} rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any additional info about this agent..." />
            </div>
          </div>
        </div>
        <div className="flex gap-3 p-6 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="flex-1 px-4 py-2.5 bg-[#548B2E] text-white rounded-lg text-sm hover:bg-[#3f6b20] disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {agent ? 'Save Changes' : 'Add Agent'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Login OTP Modal (admin-relayed) ──────────────────────────────────────────
// Shows the agent's current login code (if any) and lets the admin mint a fresh
// one to read out to the agent. Firebase phone OTP was removed from the agent
// app; agents now log in with this system OTP.
const LoginOtpModal = ({ agent, onClose }) => {
  const [loading, setLoading]       = useState(true);
  const [generating, setGenerating] = useState(false);
  const [otp, setOtp]               = useState(null); // { otp, expiresAt } | null
  const [error, setError]           = useState('');
  const [copied, setCopied]         = useState(false);
  const [now, setNow]               = useState(Date.now());

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const res = await deliveryAgentService.getLoginOtp(agent.id);
    if (res.success) setOtp(res.data);
    else setError(res.error || 'Failed to load login OTP');
    setLoading(false);
  }, [agent.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const generate = async () => {
    setGenerating(true); setError('');
    const res = await deliveryAgentService.generateLoginOtp(agent.id);
    if (res.success) setOtp({ otp: res.data.otp, expiresAt: res.data.expiresAt });
    else setError(res.error || 'Failed to generate login OTP');
    setGenerating(false);
  };

  const copy = async () => {
    if (!otp?.otp) return;
    try {
      await navigator.clipboard.writeText(otp.otp);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  const secondsLeft = otp?.expiresAt
    ? Math.max(0, Math.floor((new Date(otp.expiresAt).getTime() - now) / 1000))
    : 0;
  const expired = !!otp && secondsLeft <= 0;
  const mmss = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-amber-600" /> Login code — {agent.name}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-500">
            Share this 6-digit code with the agent so they can sign in. It expires after 10 minutes and can be used once.
          </p>

          {loading ? (
            <div className="py-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#548B2E]" /></div>
          ) : otp && !expired ? (
            <div className="text-center">
              <button
                onClick={copy}
                title="Copy code"
                className="inline-flex items-center gap-3 px-5 py-3 bg-amber-50 border border-amber-200 rounded-xl"
              >
                <span className="text-3xl font-bold tracking-[0.3em] text-amber-700">{otp.otp}</span>
                <Copy className="w-4 h-4 text-amber-600" />
              </button>
              <p className="text-xs text-gray-400 mt-2">
                {copied ? 'Copied!' : `Expires in ${mmss}`}
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-3">
              {expired ? 'The previous code expired.' : 'No active code yet.'} Generate one to share.
            </p>
          )}

          {error && <p className="text-sm text-red-600 text-center">{error}</p>}

          <button
            onClick={generate}
            disabled={generating}
            className="w-full px-4 py-2.5 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {otp && !expired ? 'Generate new code' : 'Generate code'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Availability Modal ──────────────────────────────────────────────────────
const AvailabilityModal = ({ agent, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const isOnDelivery = agent.availability === 'on_delivery';

  const handleMarkReturned = async () => {
    setSaving(true); setError('');
    const result = await deliveryAgentService.markReturned(agent.id);
    if (result.success) { onSave(); onClose(); }
    else setError(result.error || 'Failed to mark agent as returned');
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-800">Agent Availability</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
            </div>
          )}
          <p className="text-sm font-medium text-gray-700">Agent: <span className="text-[#548B2E] font-semibold">{agent.name}</span></p>
          <div className={`p-4 rounded-lg border-2 ${isOnDelivery ? 'border-blue-200 bg-blue-50' : 'border-green-200 bg-green-50'}`}>
            <p className="text-sm font-semibold text-gray-800 mb-1">
              {isOnDelivery ? '🚚 Currently On Delivery' : '🏭 At Warehouse'}
            </p>
            {isOnDelivery && agent.currentOrderId && (
              <p className="text-xs text-gray-600">Delivering Order #{agent.currentOrderId}</p>
            )}
            {isOnDelivery && agent.queuedOrderId && (
              <p className="text-xs text-gray-600 mt-1">Queued: Order #{agent.queuedOrderId}</p>
            )}
            {isOnDelivery && !agent.currentOrderId && (
              <p className="text-xs text-gray-500">Travelling back to warehouse</p>
            )}
          </div>
          {isOnDelivery && (
            <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <p className="text-xs text-amber-700">
                Click <strong>Mark Returned</strong> only when the agent has physically arrived back at the warehouse. This will automatically assign their next order.
              </p>
            </div>
          )}
        </div>
        <div className="flex gap-3 p-6 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          {isOnDelivery && (
            <button onClick={handleMarkReturned} disabled={saving}
              className="flex-1 px-4 py-2.5 bg-[#548B2E] text-white rounded-lg text-sm hover:bg-[#3f6b20] disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Mark Returned
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Registration Requests Modal ─────────────────────────────────────────────
const RegistrationRequestsModal = ({ onClose, onReviewed, newCount = 0 }) => {
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [busyId,   setBusyId]   = useState(null);
  const [error,    setError]    = useState('');
  const [rejecting, setRejecting] = useState({ id: null, reason: '' });
  const [highlightActive, setHighlightActive] = useState(false);

  useEffect(() => {
    if (newCount > 0) {
      setHighlightActive(true);
      const t = setTimeout(() => setHighlightActive(false), 3000);
      return () => clearTimeout(t);
    }
  }, [newCount]);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await deliveryAgentService.getRegistrationRequests('pending');
    if (res.success) setRequests(res.data);
    else setError(res.error || 'Failed to load requests');
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (req) => {
    setBusyId(req.id); setError('');
    const res = await deliveryAgentService.approveRegistrationRequest(req.id);
    if (res.success) { await load(); onReviewed(); }
    else setError(res.error || 'Failed to approve');
    setBusyId(null);
  };

  const submitReject = async () => {
    if (!rejecting.id) return;
    setBusyId(rejecting.id); setError('');
    const res = await deliveryAgentService.rejectRegistrationRequest(rejecting.id, rejecting.reason);
    if (res.success) { setRejecting({ id: null, reason: '' }); await load(); onReviewed(); }
    else setError(res.error || 'Failed to reject');
    setBusyId(null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Pending Registration Requests</h2>
              <p className="text-xs text-gray-500">Review and approve new agent sign-ups</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-3">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
            </div>
          )}

          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div>
          ) : requests.length === 0 ? (
            <div className="py-10 text-center">
              <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No pending requests right now.</p>
            </div>
          ) : (
            requests.map((req, index) => (
              <div key={req.id} className={`rounded-xl p-4 transition-colors ${highlightActive && index < newCount ? 'border-2 border-orange-400 ring-1 ring-orange-300 bg-orange-50' : 'border border-gray-200 hover:border-[#548B2E]/40'}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <p className="font-semibold text-gray-800 truncate">{req.name}</p>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 font-medium">Pending</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                      <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-gray-400" />+91 {req.phone}</div>
                      <div className="flex items-center gap-1.5"><Bike className="w-3.5 h-3.5 text-gray-400" />{(req.vehicleType || '').charAt(0).toUpperCase() + (req.vehicleType || '').slice(1)}</div>
                      <div className="flex items-center gap-1.5 col-span-2"><Clock className="w-3.5 h-3.5 text-gray-400" />Requested {new Date(req.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => setRejecting({ id: req.id, reason: '' })}
                      disabled={busyId === req.id}
                      className="px-3 py-1.5 text-xs border border-red-200 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5"
                    >
                      <XCircle className="w-3.5 h-3.5" />Reject
                    </button>
                    <button
                      onClick={() => handleApprove(req)}
                      disabled={busyId === req.id}
                      className="px-3 py-1.5 text-xs bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {busyId === req.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <CheckCircle className="w-3.5 h-3.5" />}
                      Approve
                    </button>
                  </div>
                </div>

                {/* Inline rejection reason form */}
                {rejecting.id === req.id && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                    <label className="block text-xs font-medium text-gray-600 flex items-center gap-1.5">
                      <MessageSquare className="w-3.5 h-3.5" />Reason for rejection <span className="text-gray-400">(optional)</span>
                    </label>
                    <textarea
                      rows={2}
                      value={rejecting.reason}
                      onChange={e => setRejecting(r => ({ ...r, reason: e.target.value }))}
                      placeholder="e.g. Phone number doesn't match our records"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setRejecting({ id: null, reason: '' })}
                        className="px-3 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50"
                      >Cancel</button>
                      <button
                        onClick={submitReject}
                        disabled={busyId === req.id}
                        className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5"
                      >
                        {busyId === req.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <XCircle className="w-3.5 h-3.5" />}
                        Confirm Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ── Assignment History Panel ────────────────────────────────────────────────
const AssignmentHistory = ({ agentId }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    deliveryAgentService.getAgentAssignments(agentId).then(r => {
      if (r.success) setAssignments(r.data);
      setLoading(false);
    });
  }, [agentId]);

  const fmt = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    const offset = date.getTimezoneOffset() * 60000;
    const adjustedDate = new Date(date.getTime() + offset);
    return adjustedDate.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };
  
  const getStateLabel = (assignment) => {
    if (assignment.isCurrent) return { label: 'Current', color: 'bg-blue-100 text-blue-700' };
    if (assignment.unassignedReason === 'delivered') return { label: 'Delivered', color: 'bg-green-100 text-green-700' };
    if (assignment.unassignedReason === 'reassigned') return { label: 'Reassigned', color: 'bg-amber-100 text-amber-700' };
    return { label: 'Cancelled', color: 'bg-red-100 text-red-700' };
  };

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  if (!assignments.length) return <p className="text-sm text-gray-400 text-center py-4">No assignment history</p>;

  return (
    <div className="space-y-2 max-h-60 overflow-y-auto">
      {assignments.map(a => {
        const state = getStateLabel(a);
        return (
          <div key={a.id} className={`p-3 rounded-lg border text-xs ${a.isCurrent ? 'border-blue-200 bg-blue-50' : 'border-gray-100 bg-gray-50'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-800">Order #{a.orderId}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${state.color}`}>
                {state.label}
              </span>
            </div>
            <p className="text-gray-600 mb-1">Assigned: {fmt(a.assignedAt)}</p>
            {a.unassignedAt && <p className="text-gray-600">Released: {fmt(a.unassignedAt)}</p>}
          </div>
        );
      })}
    </div>
  );
};

RegistrationRequestsModal.propTypes = { onClose: PropTypes.func, onReviewed: PropTypes.func, newCount: PropTypes.number };

// ── Main Component ──────────────────────────────────────────────────────────
const DeliveryAgents = ({ newCount = 0 }) => {
  const [agents,                    setAgents]                    = useState([]);
  const [stats,                     setStats]                     = useState({ total: 0, active: 0, inactive: 0, onLeave: 0 });
  const [loading,                   setLoading]                   = useState(true);
  const [search,                    setSearch]                    = useState('');
  const [statusFilter,              setStatusFilter]              = useState('');
  const [showModal,                 setShowModal]                 = useState(false);
  const [editAgent,                 setEditAgent]                 = useState(null);
  const [deleteTarget,              setDeleteTarget]              = useState(null);
  const [deleting,                  setDeleting]                  = useState(false);
  const [error,                     setError]                     = useState('');
  const [success,                   setSuccess]                   = useState('');
  const [expandedId,                setExpandedId]                = useState(null);
  const [showAvailabilityModal,      setShowAvailabilityModal]     = useState({ agent: null, show: false });
  const [pendingRequestCount,        setPendingRequestCount]       = useState(0);
  const [showRequestsModal,          setShowRequestsModal]         = useState(false);
  const [otpAgent,                   setOtpAgent]                  = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [agentsRes, statsRes] = await Promise.all([
      deliveryAgentService.getAll(),
      deliveryAgentService.getStats(),
    ]);
    if (agentsRes.success) setAgents(agentsRes.data);
    if (statsRes.success)  setStats(statsRes.data);
    setLoading(false);
  }, []);

  const fetchPendingCount = useCallback(async () => {
    const res = await deliveryAgentService.getPendingRegistrationCount();
    if (res.success) setPendingRequestCount(res.data.count || 0);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Poll pending requests every 30s so admins see new submissions without refresh.
  useEffect(() => {
    fetchPendingCount();
    const id = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(id);
  }, [fetchPendingCount]);

  // Auto-open registration modal when navigated here via a badge click.
  useEffect(() => {
    if (newCount > 0) setShowRequestsModal(true);
  }, [newCount]);

  const showMsg = (type, msg) => {
    if (type === 'success') { setSuccess(msg); setError(''); }
    else                    { setError(msg);   setSuccess(''); }
    setTimeout(() => { setSuccess(''); setError(''); }, 3000);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const result = await deliveryAgentService.remove(deleteTarget.id);
    if (result.success) { showMsg('success', `${deleteTarget.name} removed.`); fetchAll(); }
    else                  showMsg('error', result.error || 'Failed to remove agent');
    setDeleting(false);
    setDeleteTarget(null);
  };

  const filtered = agents.filter(a => {
    const matchSearch = !search || fuzzyMatch(search, a.name) || a.phone.includes(search);
    const matchStatus = !statusFilter || a.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Delivery Agents</h1>
          <p className="text-gray-600">Manage your delivery team</p>
        </div>
        <button
          onClick={() => { setEditAgent(null); setShowModal(true); }}
          className="flex items-center gap-2 bg-[#548B2E] hover:bg-[#3f6b20] text-white px-4 py-2 rounded-lg font-medium transition-colors w-full sm:w-auto justify-center"
        >
          <Plus className="w-5 h-5" /> Add Agent
        </button>
      </div>

      {/* Pending Registration Requests Banner */}
      {pendingRequestCount > 0 && (
        <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <UserPlus className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-amber-900">
                {pendingRequestCount} pending registration request{pendingRequestCount === 1 ? '' : 's'}
              </p>
              <p className="text-xs text-amber-700">New agents are waiting for approval to start delivering.</p>
            </div>
          </div>
          <button
            onClick={() => setShowRequestsModal(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium px-4 py-2 rounded-lg whitespace-nowrap"
          >
            Review Requests →
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Agents', value: stats.total,    icon: UserCheck, color: 'bg-blue-100',   icon_color: 'text-blue-600' },
          { label: 'Active',       value: stats.active,   icon: CheckCircle, color: 'bg-green-100', icon_color: 'text-green-600' },
          { label: 'Inactive',     value: stats.inactive, icon: XCircle,   color: 'bg-gray-100',   icon_color: 'text-gray-500' },
          { label: 'On Leave',     value: stats.onLeave,  icon: Clock,     color: 'bg-amber-100',  icon_color: 'text-amber-600' },
        ].map(({ label, value, icon: Icon, color, icon_color }) => (
          <div key={label} className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
                <Icon className={`w-5 h-5 ${icon_color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{value}</p>
                <p className="text-gray-500 text-sm">{label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Alerts */}
      {error   && <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 mb-4"><AlertCircle className="w-5 h-5 flex-shrink-0" />{error}</div>}
      {success && <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 mb-4"><CheckCircle className="w-5 h-5 flex-shrink-0" />{success}</div>}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder="Search by name or phone..." value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E] text-sm" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E] text-sm">
            <option value="">All Status</option>
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#548B2E] mx-auto mb-2" />
            <p className="text-gray-500">Loading agents...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <UserCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">{agents.length === 0 ? 'No agents added yet' : 'No agents match your filters'}</p>
            {agents.length === 0 && (
              <button onClick={() => { setEditAgent(null); setShowModal(true); }}
                className="mt-4 px-4 py-2 bg-[#548B2E] text-white rounded-lg text-sm hover:bg-[#3f6b20]">
                Add your first agent
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Agent', 'Contact', 'Vehicle', 'Status', 'Availability', 'Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(agent => (
                    <React.Fragment key={agent.id}>
                      <tr className={`hover:bg-gray-50 transition-colors ${expandedId === agent.id ? 'bg-gray-50' : ''}`}>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-[#f2f8eb] flex items-center justify-center flex-shrink-0">
                              <span className="text-sm font-bold text-[#548B2E]">{agent.name.charAt(0).toUpperCase()}</span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-800 text-sm">{agent.name}</p>
                              <p className="text-xs text-gray-400">ID #{agent.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm text-gray-700 flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-gray-400" /> {agent.phone}</p>
                          {agent.email && <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Mail className="w-3 h-3 text-gray-400" /> {agent.email}</p>}
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm text-gray-700 flex items-center gap-1"><Bike className="w-3.5 h-3.5 text-gray-400" />{VEHICLE_LABELS[agent.vehicleType] || agent.vehicleType}</p>
                          {agent.vehicleNo && <p className="text-xs text-gray-400 mt-0.5">{agent.vehicleNo}</p>}
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle(agent.status)}`}>
                            {statusLabel(agent.status)}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${AVAIL_STYLE[agent.availability] || 'bg-gray-100 text-gray-600'}`}>
                              {AVAIL_LABEL[agent.availability] || agent.availability}
                            </span>
                            {agent.availability === 'on_delivery' && agent.currentOrderId && (
                              <p className="text-xs text-gray-400 mt-1">Delivering: #{agent.currentOrderId}</p>
                            )}
                            {agent.availability === 'on_delivery' && !agent.currentOrderId && (
                              <p className="text-xs text-amber-500 mt-1">Returning to warehouse</p>
                            )}
                            {agent.queuedOrderId && (
                              <p className="text-xs text-blue-400 mt-1">Queued: #{agent.queuedOrderId}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-1 flex-wrap">
                            <button
                              onClick={() => setShowAvailabilityModal({ agent, show: true })}
                              className="px-2.5 py-1 bg-[#548B2E] text-white hover:bg-[#3f6b20] rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                              title="Change availability"
                            >
                              <RefreshCw className="w-3 h-3" />
                              Change
                            </button>
                            <button
                              onClick={() => setOtpAgent(agent)}
                              className="p-1.5 hover:bg-amber-50 rounded-lg text-gray-500 hover:text-amber-600 transition-colors"
                              title="Login OTP"
                            >
                              <KeyRound className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => { setEditAgent(agent); setShowModal(true); }}
                              className="p-1.5 hover:bg-[#548B2E]/10 rounded-lg text-gray-500 hover:text-[#548B2E] transition-colors"
                              title="Edit agent"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setExpandedId(expandedId === agent.id ? null : agent.id)}
                              className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
                              title="Assignment history"
                            >
                              {expandedId === agent.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => setDeleteTarget(agent)}
                              className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-500 transition-colors"
                              title="Remove agent"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedId === agent.id && (
                        <tr>
                          <td colSpan={6} className="px-6 py-4 bg-gray-50 border-b border-gray-100">
                            <p className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wider">Assignment History</p>
                            <AssignmentHistory agentId={agent.id} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Agent Modal */}
      {showModal && (
        <AgentModal
          agent={editAgent}
          onClose={() => { setShowModal(false); setEditAgent(null); }}
          onSave={() => { fetchAll(); showMsg('success', editAgent ? 'Agent updated successfully.' : 'Agent added successfully.'); }}
        />
      )}

      {/* Registration Requests Modal */}
      {showRequestsModal && (
        <RegistrationRequestsModal
          onClose={() => { setShowRequestsModal(false); fetchPendingCount(); }}
          onReviewed={() => { fetchPendingCount(); fetchAll(); }}
          newCount={newCount}
        />
      )}

      {/* Availability Modal */}
      {showAvailabilityModal.show && showAvailabilityModal.agent && (
        <AvailabilityModal
          agent={showAvailabilityModal.agent}
          onClose={() => setShowAvailabilityModal({ agent: null, show: false })}
          onSave={() => { fetchAll(); showMsg('success', `${showAvailabilityModal.agent.name}'s availability updated.`); }}
        />
      )}

      {/* Login OTP (admin-relayed) */}
      {otpAgent && (
        <LoginOtpModal agent={otpAgent} onClose={() => setOtpAgent(null)} />
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-center mb-2">Remove Agent</h3>
            <p className="text-gray-600 text-center text-sm mb-6">
              Are you sure you want to remove <strong>{deleteTarget.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 text-sm">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm flex items-center justify-center gap-2">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

DeliveryAgents.propTypes = { newCount: PropTypes.number };
export default DeliveryAgents;